import { Code, ConnectError } from '@connectrpc/connect'
import {
  IntradayOrderWriteBlockedReason,
  IntradayOrderWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { TFunction } from 'i18next'

import { readAppErrorInfo } from '~/libs/appErrorInfo'
import { intradayBlockedReasonMessage } from '~/models/intradayServerError'

/**
 * ワークフローから開いたまま、 そのワークフローが完了・削除・中断した後の札作成の拒否の文言
 * (AC-BIN-057、 #12594 決定 2)。 作成以外の操作の文言 (AC-WFW-014) と違い「入札を追加できません」 と言う。
 */
const WORKFLOW_CLOSED_CREATE_KEYS: Partial<
  Record<IntradayOrderWriteBlockedReason, string>
> = {
  [IntradayOrderWriteBlockedReason.WORKFLOW_COMPLETED]:
    'page:biddingIntraday.bidCreate.workflowCompleted',
  [IntradayOrderWriteBlockedReason.WORKFLOW_DELETED]:
    'page:biddingIntraday.bidCreate.workflowDeleted',
  [IntradayOrderWriteBlockedReason.WORKFLOW_SUSPENDED]:
    'page:biddingIntraday.bidCreate.workflowSuspended',
}

function workflowClosedCreateKey(err: unknown): string | undefined {
  if (!(err instanceof ConnectError)) return undefined
  const info = readAppErrorInfo(err, IntradayOrderWriteBlockedReasonSchema)
  return info === undefined
    ? undefined
    : WORKFLOW_CLOSED_CREATE_KEYS[info.reason]
}

/**
 * 札作成が、 開いたワークフローの完了・削除・中断で拒否されたか (AC-BIN-057)。 呼び出し側は
 * 作成モーダルを閉じ、 ワークフローと一覧を読み直す (画面が読み取り専用の表示に変わる)。
 */
export function isIntradayBidCreateWorkflowClosedError(err: unknown): boolean {
  return workflowClosedCreateKey(err) !== undefined
}

/**
 * 入札追加 (CreateBiddingIntradayOrder) の失敗を 受付前 / GC 超過 / その他 の
 * 固定文言に訳す (spec intraday-bidding.md AC-057)。 内部処理のシステムメッセージ
 * (ConnectError の `message`) を画面にそのまま連結しない (= spot 側
 * `translateSpotWriteError` と同じ「エラーコード → 固定文言」方式に統一)。
 *
 * 受付前 (AC-BIN-089c) と GC 超過はどちらも FailedPrecondition で届くため、
 * まず `google.rpc.ErrorInfo` detail の reason で引き分ける。 フォーミュラの ÷ 0 は
 * InvalidArgument で届くので、 reason は code によらず先に見る。 reason が無い
 * FailedPrecondition は従来どおり GC 超過相当として扱う (backend は GC ガードに
 * まだ reason を載せていないため)。
 *
 * 既知の理由 (受付前 / GC 超過 / フォーミュラ・手数料の理由) なら固定文言、 分からなければ
 * undefined を返す。 一括作成のバナーが、 理由の分からない失敗を文言の比較に頼らず見分ける。
 */
export function intradayBidCreateErrorReason(
  err: unknown,
  t: TFunction,
): string | undefined {
  const workflowClosedKey = workflowClosedCreateKey(err)
  if (workflowClosedKey !== undefined) return t(workflowClosedKey)
  if (err instanceof ConnectError) {
    const reasonMessage = intradayBlockedReasonMessage(err, t)
    if (reasonMessage !== undefined) return reasonMessage
    if (err.code === Code.FailedPrecondition) {
      return t('page:biddingIntraday.bidCreate.gcExceeded')
    }
  }
  return undefined
}

/**
 * サーバーが reason (ErrorInfo) で明示した理由だけを固定文言にする。 reason の無い
 * FailedPrecondition を GC 超過に丸める [[intradayBidCreateErrorReason]] と違い、 丸めた
 * ものは undefined のまま返す。 一括作成の全件失敗トーストが、 実際の原因 (上限数量超過・
 * 完了済ケース等) と食い違う GC 超過の文言を出さないために使う。
 */
export function intradayBidCreateExplicitReason(
  err: unknown,
  t: TFunction,
): string | undefined {
  const workflowClosedKey = workflowClosedCreateKey(err)
  if (workflowClosedKey !== undefined) return t(workflowClosedKey)
  return err instanceof ConnectError
    ? intradayBlockedReasonMessage(err, t)
    : undefined
}

/** [[intradayBidCreateErrorReason]] で分からない失敗は汎用の失敗文言にする。 */
export function translateIntradayBidCreateError(
  err: unknown,
  t: TFunction,
): string {
  return (
    intradayBidCreateErrorReason(err, t) ??
    t('page:biddingIntraday.bidCreate.error')
  )
}
